import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ParamsTimeType } from '@op/types/common';
import type { AnchorMode, IEventItem, IHighPriorityEventItem } from '@op/components/ChartsEvents/types';
import { resolveOpinionChartXAxisValue } from './utils/resolveOpinionChartXAxisValue';

const SERIES_NAME = 'TopTipsEvent';

interface MarkAreaState {
  renderMarkArea: { width: string; left: string; height?: string; top?: string };
  eventLineShow: boolean;
  markAreaShow: boolean;
  markAreaTimes: string;
}

interface IUseOpinionEventItemPositionProps {
  myChart: any;
  xAxisValue: string;
  format: string;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
    anchorMode?: AnchorMode;
    shouldHide?: boolean;
  }) => void;
  anchorMode?: AnchorMode;
  event?: IEventItem | IHighPriorityEventItem;
}

/** 情报侧事件气泡定位：兼容月粒度 YYYY-MM x 轴 */
export function useOpinionEventItemPosition({
  myChart,
  xAxisValue,
  format,
  dateType,
  xAxisData,
  backPositionInfo,
  yAxisMin = false,
  anchorMode = 'start',
  event,
}: IUseOpinionEventItemPositionProps) {
  const eventBoxRef = useRef<any>(null);
  const eventLineRef = useRef<any>(null);
  const currentItemPosition = useRef<number>(0);

  const [markAreaState, setMarkAreaState] = useState<MarkAreaState>({
    renderMarkArea: { width: '0px', left: '0px' },
    eventLineShow: false,
    markAreaShow: false,
    markAreaTimes: '',
  });

  const validateTimeFormat = (time: string): boolean => {
    return moment(time, format, true).isValid();
  };

  const validateTimeRange = (startTime: string, end: string): boolean => {
    return moment(end).isSameOrAfter(moment(startTime));
  };

  const getTimeInXAxisData = (time: string): { isInAxis: boolean; realValue: string } => {
    return resolveOpinionChartXAxisValue(time, dateType, format, xAxisData);
  };

  const devWarn = (message: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[OpinionChartsEvents] ${message}`);
    }
  };

  const validateAnchorTime = (): {
    anchorTime: string;
    actualAnchorMode: AnchorMode;
    shouldHide: boolean;
  } => {
    const startTime = event?.start_time;
    const endTime = event?.end_time;

    const xAxisResult = getTimeInXAxisData(xAxisValue);
    if (!xAxisResult.isInAxis) {
      if (event && startTime) {
        const startResult = getTimeInXAxisData(startTime);
        if (startResult.isInAxis) {
          devWarn(
            `xAxisValue (${xAxisValue}) is not in xAxisData, falling back to startTime (${startResult.realValue})`,
          );
          return {
            anchorTime: startResult.realValue,
            actualAnchorMode: 'start',
            shouldHide: false,
          };
        }
      }
      devWarn(`xAxisValue (${xAxisValue}) is not in xAxisData, hiding event`);
      return { anchorTime: xAxisValue, actualAnchorMode: 'start', shouldHide: true };
    }

    if (anchorMode !== 'end') {
      return {
        anchorTime: xAxisResult.realValue,
        actualAnchorMode: anchorMode,
        shouldHide: false,
      };
    }

    if (!endTime) {
      devWarn('anchorMode is "end" but no endTime provided, falling back to start_time');
      return {
        anchorTime: xAxisResult.realValue,
        actualAnchorMode: 'start',
        shouldHide: false,
      };
    }

    if (!validateTimeFormat(endTime)) {
      devWarn(`Invalid endTime format: ${endTime}, expected: ${format}`);
      return {
        anchorTime: xAxisResult.realValue,
        actualAnchorMode: 'start',
        shouldHide: false,
      };
    }

    if (startTime && !validateTimeRange(startTime, endTime)) {
      devWarn(
        `endTime (${endTime}) is before startTime (${startTime}), falling back to start_time`,
      );
      return {
        anchorTime: xAxisResult.realValue,
        actualAnchorMode: 'start',
        shouldHide: false,
      };
    }

    const endResult = getTimeInXAxisData(endTime);
    if (!endResult.isInAxis) {
      devWarn(`endTime (${endTime}) is not in xAxisData, hiding event`);
      return {
        anchorTime: xAxisResult.realValue,
        actualAnchorMode: 'start',
        shouldHide: true,
      };
    }

    return {
      anchorTime: endResult.realValue,
      actualAnchorMode: 'end',
      shouldHide: false,
    };
  };

  function getBarLeftPosition({
    seriesName = SERIES_NAME,
    xAxis = xAxisValue,
  }: {
    seriesName?: string;
    xAxis?: string;
  } = {}) {
    const { realValue: realXAxisValue } = resolveOpinionChartXAxisValue(
      xAxis,
      dateType,
      format,
      xAxisData,
    );

    try {
      const yAxisModel = myChart.getModel().getComponent('yAxis', 0);
      let yValue = 0;
      if (yAxisModel && yAxisMin) {
        const { scale } = yAxisModel.axis;
        const extent = scale.getExtent();
        // eslint-disable-next-line prefer-destructuring
        yValue = extent[0];
      }

      const bottomPixelPosition = myChart.convertToPixel({ seriesName }, [
        realXAxisValue,
        yValue,
      ]);

      return { left: bottomPixelPosition[0], bottom: bottomPixelPosition[1] };
    } catch (error) {
      return { left: 0, bottom: 0 };
    }
  }

  function setEventBoxPosition({
    seriesName = SERIES_NAME,
    callback = true,
  }: { seriesName?: string; callback?: boolean } = {}) {
    const { anchorTime, actualAnchorMode, shouldHide } = validateAnchorTime();
    const leftPosition = getBarLeftPosition({
      seriesName,
      xAxis: anchorTime,
    });

    currentItemPosition.current = leftPosition.left;
    if (callback && eventBoxRef.current) {
      backPositionInfo({
        barLeftPosition: leftPosition.left,
        barBottomPosition: leftPosition.bottom,
        eventBox: eventBoxRef.current,
        eventLine: eventLineRef.current,
        anchorMode: actualAnchorMode,
        shouldHide,
      });
    }
  }

  useEffect(() => {
    if (myChart && xAxisData && xAxisData.length > 0) {
      setEventBoxPosition();
    }
  }, [myChart, xAxisData, xAxisValue, anchorMode]);

  const setRenderMarkArea = (value: MarkAreaState['renderMarkArea']) =>
    setMarkAreaState(prev => ({ ...prev, renderMarkArea: value }));

  const setEventLineShow = (value: boolean) =>
    setMarkAreaState(prev => ({ ...prev, eventLineShow: value }));

  const setMarkAreaShow = (value: boolean) =>
    setMarkAreaState(prev => ({ ...prev, markAreaShow: value }));

  const setMarkAreaTimes = (value: string) =>
    setMarkAreaState(prev => ({ ...prev, markAreaTimes: value }));

  return {
    eventBoxRef,
    eventLineRef,
    currentItemPosition,
    renderMarkArea: markAreaState.renderMarkArea,
    eventLineShow: markAreaState.eventLineShow,
    markAreaShow: markAreaState.markAreaShow,
    markAreaTimes: markAreaState.markAreaTimes,
    setRenderMarkArea,
    setEventLineShow,
    setMarkAreaShow,
    setMarkAreaTimes,
    setMarkAreaState,
    getBarLeftPosition,
    setEventBoxPosition,
  };
}
